import { useAdminAuthStore } from '../stores/authStore';
import type { User, VideoJob, DashboardStats } from '../types';

const API_BASE = '/api/v1/admin';

class AdminApiClient {
  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    const token = useAdminAuthStore.getState().token;
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers: {
        ...this.getHeaders(),
        ...options.headers,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || 'Request failed');
    }

    return data.data;
  }

  // Auth
  async login(email: string, password: string): Promise<{ user: User & { isAdmin: boolean }; token: string }> {
    const response = await fetch(`${API_BASE}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error?.message || 'Login failed');
    return data.data;
  }

  // Dashboard
  async getStats(): Promise<DashboardStats> {
    return this.request('/stats');
  }

  // Users
  async getUsers(params?: { limit?: number; offset?: number; search?: string }): Promise<{ users: User[]; total: number }> {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.offset) query.set('offset', params.offset.toString());
    if (params?.search) query.set('search', params.search);
    return this.request(`/users?${query}`);
  }

  async getUser(id: string): Promise<User> {
    return this.request(`/users/${id}`);
  }

  async updateUser(id: string, data: Partial<User>): Promise<User> {
    return this.request(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteUser(id: string): Promise<void> {
    return this.request(`/users/${id}`, { method: 'DELETE' });
  }

  async setUserCredits(id: string, credits: number): Promise<User> {
    return this.request(`/users/${id}/credits`, {
      method: 'PUT',
      body: JSON.stringify({ credits }),
    });
  }

  // Jobs
  async getJobs(params?: { limit?: number; offset?: number; status?: string; userId?: string }): Promise<{ jobs: VideoJob[]; total: number }> {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.offset) query.set('offset', params.offset.toString());
    if (params?.status) query.set('status', params.status);
    if (params?.userId) query.set('userId', params.userId);
    return this.request(`/jobs?${query}`);
  }

  async getJob(id: string): Promise<VideoJob> {
    return this.request(`/jobs/${id}`);
  }

  async cancelJob(id: string): Promise<void> {
    return this.request(`/jobs/${id}/cancel`, { method: 'POST' });
  }

  async deleteJob(id: string): Promise<void> {
    return this.request(`/jobs/${id}`, { method: 'DELETE' });
  }

  async retryJob(id: string): Promise<VideoJob> {
    return this.request(`/jobs/${id}/retry`, { method: 'POST' });
  }
}

export const adminApi = new AdminApiClient();
